import './index.css';
import React from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { store } from './redux/index';
import { App } from './components/App/App';
import { BrowserRouter as Router } from 'react-router-dom';
import { DesktopPage } from './pages/DesktopPage/DesktopPage';
import { getErrorMessage } from './utils/errors';
import { getPlatform, initPlatform, Platform } from './platform/platform';

type AppDebug = {
    scriptInitialized?: boolean;
    renderCalled?: boolean;
    strictModeActive?: boolean;
    componentMounted?: boolean;
    errors: string[];
};

const debug = {
    get: (): AppDebug | undefined => (window as any).__APP_DEBUG__,
    
    set: (key: keyof AppDebug, value: any): null => {
        const debugObj = debug.get();
        if (!debugObj) return null;
        
        debugObj[key] = value;
        return null;
    },
    
    addError: (error: string): void => {
        const debugObj = debug.get();
        if (!debugObj) return;
        
        debugObj.errors.push(error);
    },
    
    init: (): void => {
        debug.set('scriptInitialized', true);
    }
};


const renderApplication = (): void => {
    const container = document.getElementById('root');
    if (!container) {
        debug.addError('Root container not found');
        return;
    }

    debug.set('renderCalled', true);
    
    const root = createRoot(container);
    const isDevEnv = process.env.REACT_APP_ENV === 'DEV';
    const platform = getPlatform();
    
    const app = (
        <React.StrictMode>
            {debug.set('strictModeActive', true)}
            <Provider store={store}>
                <Router>
                    { platform !== Platform.Desktop || isDevEnv ? <App /> : <DesktopPage /> }
                </Router>
            </Provider>
        </React.StrictMode>
    );
    
    root.render(app);
    debug.set('componentMounted', true);
};

const bootstrapApplication = async (): Promise<void> => {
    debug.init();
    
    try {
        await initPlatform();
        renderApplication();
    } catch (error) {
        debug.addError(`Bootstrap error: ${getErrorMessage(error)}`);
        console.error('Application bootstrap failed', error);
    }
};

void bootstrapApplication();